import React from 'react';
import { CheckCircle, Eye, Trash2, Search, RefreshCw, Filter, Star, Mail, Calendar, Zap, RotateCcw, AlertOctagon, Smartphone, Share2, Play, Stethoscope, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { hasVideoFile, resolveThumbnailUrl } from '../../utils/hlsPlayer';

// Admin dashboard "videos" tab (extracted from AdminPage).
export const AdminVideosTab = ({
  categoryFilter,
  dateFilter,
  filteredVideos,
  handleApproveVideo,
  handleCleanupOrphans,
  handleDeleteVideo,
  handleFaststartRemux,
  handleFeaturedAction,
  handleReModerateRejected,
  handleReassignVideo,
  handleRebuildFromBunny,
  handleRestoreAllDeleted,
  handleRestoreVideo,
  handleToggleLyric,
  handleWarmAllSocialCaches,
  handleWarmOne,
  openDiagnose,
  reModerating,
  remuxingIds,
  resetFilters,
  searchQuery,
  setCategoryFilter,
  setDateFilter,
  setPreviewVideo,
  setSearchQuery,
  setStatusFilter,
  statusFilter,
  videos,
}) => (
  <div className="space-y-4">
    {/* Filters Bar */}
    <div className="flex flex-wrap items-center gap-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
      <div className="flex items-center gap-2 text-zinc-400">
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">Filters:</span>
      </div>

      {/* Search */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Search videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />
      </div>

      {/* Date Filter */}
      <Select value={dateFilter} onValueChange={setDateFilter}>
        <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <Calendar className="w-4 h-4 mr-2" />
          <SelectValue placeholder="Date" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="all" className="text-white">All Time</SelectItem>
          <SelectItem value="today" className="text-white">Today</SelectItem>
          <SelectItem value="week" className="text-white">This Week</SelectItem>
          <SelectItem value="month" className="text-white">This Month</SelectItem>
        </SelectContent>
      </Select>

      {/* Status Filter */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700">
          <SelectItem value="active" className="text-white">Active</SelectItem>
          <SelectItem value="all" className="text-white">All Status</SelectItem>
          <SelectItem value="approved" className="text-white">Approved</SelectItem>
          <SelectItem value="pending" className="text-white">Pending</SelectItem>
          <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
          <SelectItem value="deleted" className="text-white">Deleted</SelectItem>
        </SelectContent>
      </Select>

      {/* Category/Genre Filter */}
      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
        <SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white text-sm">
          <SelectValue placeholder="Genre" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
          <SelectItem value="all" className="text-white">All Genres</SelectItem>
          <SelectItem value="Pop" className="text-white">Pop</SelectItem>
          <SelectItem value="Rock" className="text-white">Rock</SelectItem>
          <SelectItem value="Hip-Hop / Rap" className="text-white">Hip-Hop / Rap</SelectItem>
          <SelectItem value="R&B / Soul" className="text-white">R&B / Soul</SelectItem>
          <SelectItem value="Electronic / Dance (EDM)" className="text-white">Electronic / Dance</SelectItem>
          <SelectItem value="Jazz" className="text-white">Jazz</SelectItem>
          <SelectItem value="Classical" className="text-white">Classical</SelectItem>
          <SelectItem value="Country" className="text-white">Country</SelectItem>
          <SelectItem value="Reggae" className="text-white">Reggae</SelectItem>
          <SelectItem value="Blues" className="text-white">Blues</SelectItem>
          <SelectItem value="Latin" className="text-white">Latin</SelectItem>
          <SelectItem value="Folk" className="text-white">Folk</SelectItem>
          <SelectItem value="Metal" className="text-white">Metal</SelectItem>
          <SelectItem value="Punk" className="text-white">Punk</SelectItem>
          <SelectItem value="Funk" className="text-white">Funk</SelectItem>
          <SelectItem value="Gospel / Christian" className="text-white">Gospel / Christian</SelectItem>
          <SelectItem value="Indie / Alternative" className="text-white">Indie / Alternative</SelectItem>
          <SelectItem value="K-Pop" className="text-white">K-Pop</SelectItem>
          <SelectItem value="Afrobeats" className="text-white">Afrobeats</SelectItem>
          <SelectItem value="World Music" className="text-white">World Music</SelectItem>
          <SelectItem value="Other" className="text-white">Other</SelectItem>
        </SelectContent>
      </Select>

      <Button 
        variant="outline" 
        size="sm" 
        onClick={resetFilters}
        className="bg-transparent border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white"
      >
        Reset
      </Button>

      <button
        onClick={handleCleanupOrphans}
        className="px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 text-xs font-medium flex items-center gap-1.5 border border-amber-500/30"
        title="Delete videos that are missing their stored file (failed uploads)"
        data-testid="cleanup-orphans-btn"
      >
        <AlertOctagon className="w-3.5 h-3.5" />
        <span>Clean up broken</span>
      </button>

      <button
        onClick={handleRestoreAllDeleted}
        className="px-3 py-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-medium flex items-center gap-1.5 border border-emerald-500/30"
        title="Restore every currently soft-deleted video. Owner attribution is preserved."
        data-testid="restore-all-deleted-btn"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span>Restore all deleted</span>
      </button>

      <button
        onClick={handleRebuildFromBunny}
        className="px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 text-xs font-medium flex items-center gap-1.5 border border-purple-500/30"
        title="Rebuild missing video records from the Bunny library. Relinks blank rows AND inserts truly-missing ones."
        data-testid="rebuild-from-bunny-btn"
      >
        <Zap className="w-3.5 h-3.5" />
        <span>Rebuild from Bunny</span>
      </button>

      <button
        onClick={handleWarmAllSocialCaches}
        className="px-3 py-1.5 rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 text-xs font-medium flex items-center gap-1.5 border border-blue-500/30"
        title="Tell Facebook & LinkedIn to refresh share previews for every approved video"
        data-testid="warm-social-cache-btn"
      >
        <Share2 className="w-3.5 h-3.5" />
        <span>Refresh share previews</span>
      </button>

      {/* Bulk re-moderate — only surfaced when the Rejected filter is
          active, since that's the only case where it's useful (and
          on Approved/All it would be confusing). Lets admins recover
          creators whose work was caught by a previous moderation prompt
          (e.g. iter 80's Violence-or-gore → Active-violence narrowing). */}
      {statusFilter === 'rejected' && (
        <button
          onClick={handleReModerateRejected}
          className="px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 text-xs font-medium flex items-center gap-1.5 border border-purple-500/30 disabled:opacity-50 disabled:cursor-wait"
          disabled={reModerating}
          title="Re-run the moderation pipeline against every rejected video. Videos that now pass will flip to Approved automatically."
          data-testid="re-moderate-rejected-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${reModerating ? 'animate-spin' : ''}`} />
          <span>{reModerating ? 'Re-moderating…' : 'Bulk re-moderate'}</span>
        </button>
      )}

      <span className="text-xs text-zinc-500 ml-auto">{filteredVideos.length} videos</span>
    </div>

    {/* Videos Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredVideos.map((video) => (
        <div key={video.id} className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="aspect-video bg-zinc-800 relative">
            {/* Shared resolver: prefers custom_thumbnail_url →
                thumbnail_url → thumbnail_path → AUDIVUE placeholder.
                Previously this only checked thumbnail_path so Bunny
                videos (no legacy file path) rendered as empty cards. */}
            <img
              src={resolveThumbnailUrl(video)}
              alt={video.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-white truncate flex-1">{video.title}</h4>
              {video.is_deleted && (
                <>
                  <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400 flex-shrink-0" data-testid={`deleted-badge-${video.id}`}>DELETED</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRestoreVideo(video); }}
                    className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 flex-shrink-0 transition-colors"
                    title="Restore — un-soft-delete and reset status to approved if needed"
                    data-testid={`restore-btn-${video.id}`}
                  >
                    Restore
                  </button>
                </>
              )}
            </div>
            <p className="text-sm text-zinc-500">by {video.artist || video.username}</p>
            {video.user_email && (
              <p className="text-xs text-zinc-600 truncate mt-1">{video.user_email}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {video.views || 0}
                </span>
                <span>{video.genre}</span>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end -mx-0.5">
                {video.user_email && (
                  <a 
                    href={`mailto:${video.user_email}?subject=Regarding your video "${video.title}" on AUDIVUE`}
                    className="p-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
                    title="Contact Creator"
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => handleToggleLyric(video)}
                  className={`px-2 py-1.5 rounded text-xs font-semibold whitespace-nowrap ${
                    video.is_lyric
                      ? 'bg-amber-500/30 hover:bg-amber-500/40 text-amber-300'
                      : 'bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300'
                  }`}
                  title={video.is_lyric ? 'Lyric video — click to unmark' : 'Mark as lyric video (push to bottom)'}
                  data-testid={`toggle-lyric-${video.id}`}
                >
                  {video.is_lyric ? 'Lyric' : 'Not Lyric'}
                </button>
                {hasVideoFile(video) ? (
                  // Overlay-consistency guard (iter 113): if Bunny
                  // has an asset (guid || hls_url) treat the row as
                  // healthy for chip purposes even when status is
                  // 'failed' — a stale failure webhook shouldn't
                  // flip this to the red "Failed" chip when the
                  // creator-facing card will (correctly) show it
                  // as processing/playable. Same rule as
                  // StudioPage's overlay guard.
                  (
                    video.status === 'processing'
                    || (video.bunny_video_guid && !video.hls_url)
                  ) ? (
                    <span
                      className="px-2 py-1.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300/90 flex items-center gap-1 whitespace-nowrap"
                      title="Video is still transcoding on Bunny — playback will be ready shortly."
                      data-testid={`video-processing-${video.id}`}
                    >
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      <span className="hidden sm:inline">Processing</span>
                    </span>
                  ) : (video.status === 'failed' && !video.bunny_video_guid && !video.hls_url) ? (
                    <span
                      className="px-2 py-1.5 rounded text-xs font-medium bg-red-500/15 text-red-300/90 flex items-center gap-1 whitespace-nowrap"
                      title={video.processing_error || 'Processing failed'}
                      data-testid={`video-failed-${video.id}`}
                    >
                      <AlertOctagon className="w-3 h-3" />
                      <span className="hidden sm:inline">Failed</span>
                    </span>
                  ) : video.video_path ? (
                    <button
                      onClick={() => handleFaststartRemux(video)}
                      disabled={remuxingIds.has(video.id) || video.remux_status === 'running'}
                      className={`px-2 py-1.5 rounded text-xs font-medium flex items-center gap-1 whitespace-nowrap disabled:cursor-wait ${
                        video.remux_status === 'done'
                          ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                          : video.remux_status === 'failed'
                          ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                          : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300'
                      } ${video.remux_status === 'running' ? 'opacity-60' : ''}`}
                      title={
                        video.remux_status === 'running' ? 'Remux running in the background'
                        : video.remux_status === 'done' ? `Last remuxed at ${video.remux_completed_at || ''}`
                        : video.remux_status === 'failed' ? `Last attempt failed: ${video.remux_error || ''}`
                        : 'Fix mobile playback (lossless ffmpeg +faststart remux)'
                      }
                      data-testid={`faststart-remux-${video.id}`}
                    >
                      {(remuxingIds.has(video.id) || video.remux_status === 'running') ? (
                        <>
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          <span className="hidden sm:inline">Fixing…</span>
                        </>
                      ) : video.remux_status === 'done' ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span className="hidden sm:inline">Fixed</span>
                        </>
                      ) : (
                        <>
                          <Smartphone className="w-3 h-3" />
                          <span className="hidden sm:inline">Fix mobile</span>
                        </>
                      )}
                    </button>
                  ) : (
                    // Bunny HLS-only video — no legacy file to remux, but it's playable.
                    <span
                      className="px-2 py-1.5 rounded text-xs font-medium bg-green-500/15 text-green-300/90 flex items-center gap-1 whitespace-nowrap"
                      title="Streaming via Bunny HLS — no legacy file to remux."
                      data-testid={`video-hls-${video.id}`}
                    >
                      <CheckCircle className="w-3 h-3" />
                      <span className="hidden sm:inline">HLS</span>
                    </span>
                  )
                ) : (
                  <span
                    className="px-2 py-1.5 rounded text-xs font-medium bg-amber-500/15 text-amber-300/90 flex items-center gap-1 whitespace-nowrap"
                    title="Upload never finished — there's no video file in storage. Delete and ask the artist to re-upload."
                    data-testid={`no-video-stored-${video.id}`}
                  >
                    <AlertOctagon className="w-3 h-3" />
                    <span className="hidden sm:inline">No file</span>
                  </span>
                )}
                <button 
                  onClick={() => setPreviewVideo(video)}
                  disabled={!hasVideoFile(video)}
                  className="p-1.5 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={hasVideoFile(video) ? 'Watch video' : 'No video file available'}
                  data-testid={`preview-video-${video.id}`}
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleWarmOne(video)}
                  className="p-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-300"
                  title="Refresh Facebook & LinkedIn share preview for this video"
                  data-testid={`refresh-fb-${video.id}`}
                >
                  <Share2 className="w-4 h-4" />
                </button>
                {/* Approve / Restore — only shown for rejected videos so admins
                    can un-reject without leaving this list. Backend's
                    /admin/videos/{id}/approve just sets status=approved
                    regardless of current status, so it works for rejects too. */}
                {video.status === 'rejected' && (
                  <button
                    onClick={() => handleApproveVideo(video.id)}
                    className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400"
                    title="Restore (un-reject) — sets status to Approved"
                    data-testid={`restore-video-${video.id}`}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleReassignVideo(video)}
                  className="p-1.5 rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-300"
                  title={`Reassign to a different artist (current: ${video.username || '?'})`}
                  data-testid={`reassign-video-${video.id}`}
                >
                  <User className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleFeaturedAction(video)}
                  className={`p-1.5 rounded ${
                    video.featured
                      ? 'bg-fuchsia-500/30 hover:bg-fuchsia-500/40 text-fuchsia-200'
                      : video.review_status === 'pending'
                      ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300'
                      : video.review_status === 'rejected'
                      ? 'bg-zinc-500/20 hover:bg-zinc-500/30 text-zinc-400'
                      : 'bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-300'
                  }`}
                  title={
                    video.featured
                      ? 'Featured — click to manage'
                      : video.review_status === 'pending'
                      ? 'Pending review — click to approve/reject'
                      : video.review_status === 'rejected'
                      ? `Rejected from Featured: ${video.rejection_reason || 'no reason'} — click to re-pull into review`
                      : 'Pull into Featured review queue'
                  }
                  data-testid={`feature-action-${video.id}`}
                >
                  <Star className={`w-4 h-4 ${video.featured ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={() => openDiagnose(video)}
                  className="p-1.5 rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-300"
                  title="Diagnose video file (ffprobe + moov atom scan)"
                  data-testid={`diagnose-video-${video.id}`}
                >
                  <Stethoscope className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteVideo(video.id)}
                  className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400"
                  title="Delete Video"
                  data-testid={`delete-video-${video.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);
